<script lang="ts" setup>
  import { computed, ref, useTemplateRef, watch } from 'vue'

  import { useRoute, useRouter } from 'vue-router'

  import { useFirestore } from 'vuefire'

  import { doc, setDoc } from 'firebase/firestore'

  import useOuraAuth from '#web-oura-tools/auth'

  import { useServiceLaunch } from '#ourahub-intra/composables/serviceLaunch'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  import { consumePendingPreset, getGroupPresetId, resolvePresetId } from '#ourahub-intra/utilities'

  import type { Service } from '#ourahub-intra/types/services'
  import type { OuraUser } from '#web-oura-tools/types/user'

  const pinnedPreset = {
    id: 'pinned',
    name: 'Pinned items',
    icon: 'mdi-pin',
    color: 'deep-orange',
    services: [],
  }

  const props = defineProps<{
    rail: boolean
    user: OuraUser
  }>()

  const emits = defineEmits<{
    logout: []
  }>()

  const route = useRoute()
  const router = useRouter()

  const { launchService } = useServiceLaunch()

  const ouraAuth = useOuraAuth()

  const firestore = useFirestore()

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()

  const serviceDetails = ref<Service>()
  const recentServices = ref<Service[]>([])

  const confirmDialog = useTemplateRef('confirmDialog')

  // URL preset (as ?preset=x). Always applied for this session; only saved when the user has no saved preset yet.
  const pendingPreset = consumePendingPreset()
  const presetOverride = ref(pendingPreset)

  // Precedence: session override (URL bootstrap or manual pick) → saved pref → group default → pinned.
  const activePresetId = computed(() =>
    resolvePresetId({
      override: presetOverride.value,
      savedPreset: ouraHubStore.accountPrefs?.presetTemplate,
      fallback: getGroupPresetId(props.user.activeGroups) ?? pinnedPreset.id,
    }),
  )

  const visibleCollections = computed(() => {
    const collections = servicesStore.collectionsList?.core ?? []

    if (!ouraAuth.user.value?.isBPOAgentUser) {
      return collections
    }

    return collections
      .map((config) => ({ ...config, presets: config.presets.filter((p) => p.allowForBPOAgent) }))
      .filter((config) => config.presets.length)
  })

  const selectedPreset = computed(
    () =>
      visibleCollections.value.flatMap((config) => config.presets).find((p) => p.id === activePresetId.value) ||
      pinnedPreset,
  )

  function persistPreset(presetId: string) {
    setDoc(doc(firestore, 'account-prefs', props.user.id), { presetTemplate: presetId }, { merge: true })
  }

  // Persist a first-time URL preset as the saved preference, but only when the user has no saved preset yet.
  if (pendingPreset && !ouraHubStore.accountPrefs?.presetTemplate) {
    persistPreset(pendingPreset)
  }

  function selectPreset(presetId: string) {
    // Apply immediately; converges with the saved pref once the write propagates.
    presetOverride.value = presetId

    persistPreset(presetId)
  }

  const visibleServices = computed(() => {
    const items =
      selectedPreset.value.id === 'pinned'
        ? (ouraHubStore.accountPrefs?.pinnedServices ?? [])
        : selectedPreset.value.services

    const filteredServices = items
      .map((i) => servicesStore.servicesList.find((s) => s.id === i))
      .filter((s) => Boolean(s)) as Service[]

    return servicesStore.categoriesList?.core.reduce(
      (acc: { category: string; services: Service[] }[], category: { name: string }) => {
        const categoryServices = filteredServices.filter((s) => s.category === category.name)

        if (categoryServices.length) {
          acc.push({
            category: category.name,
            services: categoryServices,
          })
        }
        return acc
      },
      [],
    )
  })

  watch(
    () => visibleServices.value,
    (curServices) => {
      ouraHubStore.presetCategories = curServices?.map((s) => s.category) ?? []
    },
    { immediate: true },
  )

  watch(
    [() => route.name, () => servicesStore.servicesList],
    ([name, servicesList]) => {
      if (!recentServices.value.length) {
        recentServices.value = servicesList.filter((s) => ouraHubStore.accountPrefs?.recentServices?.includes(s.id))
      }

      if (name && name !== 'home') {
        if (recentServices.value.find((s) => s.id === name)) {
          return
        }

        recentServices.value.push(servicesList.find((s) => name === s.id)!)

        if (recentServices.value.length > 3) {
          recentServices.value.shift()
        }

        ouraHubStore.updateAccountPrefs({ recentServices: recentServices.value.map((s) => s.id) })
      }
    },
    { immediate: true },
  )

  function goToCatalog() {
    router.push({ name: 'home', params: { env: ouraAuth.appEnv.value } })
  }

  function canLaunchPublic(service: Service) {
    const stage = servicesStore.publicStage(service)

    return Boolean(stage) && servicesStore.canAccessService(service, stage)
  }

  function needsPermissions(service: Service) {
    const stage = servicesStore.publicStage(service)

    return Boolean(stage) && !servicesStore.hasAccessRights(service, stage)
  }

  async function navigateToService(service: Service) {
    const stage = servicesStore.publicStage(service)

    if (!stage) {
      return
    }

    if (!servicesStore.isLaunchableInNewTab(service)) {
      window.history.pushState(undefined, '', '/')

      if (route.name === service.id && route.params.env === stage) {
        return
      }
    }

    await launchService(service, stage)
  }

  function openFeedbackDialog() {
    const service = servicesStore.servicesList.find((s) => route.name === s.id) ?? null

    confirmDialog.value?.open(
      'Report an issue or give feedback',
      'To report an issue or give feedback for <b>"' +
        (service?.name || 'OuraHub') +
        '"</b>, please send a Slack message to: <b>' +
        (service?.contact || '#help-oura-tooling') +
        '</b>',
      {
        buttonTrueText: 'Close',
        buttonTrueColor: 'primary',
        buttonFalseText: '',
      },
    )
  }
</script>

<template lang="html">
  <v-navigation-drawer
    permanent
    width="300"
    theme="dark"
    rail-width="96"
    data-intro-tour-id="side-bar"
    class="text-white bg-transparent px-3"
    :rail="props.rail"
  >
    <div class="corner" />

    <div class="d-flex flex-column fill-height">
      <v-divider class="mt-0 mb-3" />

      <v-list nav class="flex-shrink-0">
        <v-tooltip location="right" text="All apps and tools" :disabled="!props.rail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              class="mb-2"
              prepend-icon="mdi-apps"
              title="All apps and tools"
              active-class="bg-blue-grey-darken-1"
              :active="route.name === 'home'"
              @click="goToCatalog()"
            />
          </template>
        </v-tooltip>
      </v-list>

      <v-divider class="mt-n2 mb-4" />

      <v-list v-if="recentServices.length" nav class="flex-shrink-0 mt-n4">
        <template v-if="recentServices.length">
          <v-list-subheader v-if="!props.rail" color="grey" title="Recently opened" />

          <v-tooltip
            v-for="loadedService in recentServices"
            :key="loadedService.id"
            location="right"
            :disabled="!props.rail"
            :text="loadedService?.name || 'Unknown application'"
          >
            <template #activator="{ props: tooltipProps }">
              <v-list-item
                v-bind="tooltipProps"
                active-class="bg-blue-grey-darken-1"
                :title="loadedService.name"
                :prepend-icon="loadedService.icon"
                :active="route.name === loadedService.id"
                :disabled="!canLaunchPublic(loadedService)"
                @click="navigateToService(loadedService)"
              />
            </template>
          </v-tooltip>
        </template>

        <v-divider class="mt-2" />
      </v-list>

      <v-list
        nav
        class="flex-shrink-0 flex-shrink-1"
        style="background: rgba(255, 255, 255, 0.05); border-radius: 16px; overflow-y: auto; min-height: 0"
        :class="props.rail ? '' : 'ma-2'"
      >
        <v-list-subheader v-if="!props.rail" color="grey" title="Shortcuts" class="bg-transparent" />

        <v-menu offset="8" location="bottom start">
          <template #activator="{ props: menuProps }">
            <v-tooltip location="right" text="Shortcuts" :disabled="!props.rail">
              <template #activator="{ props: tooltipProps }">
                <v-text-field
                  rounded
                  readonly
                  v-bind="{ ...menuProps, ...tooltipProps }"
                  hide-details
                  density="compact"
                  variant="outlined"
                  style="cursor: pointer"
                  :model-value="selectedPreset.name"
                  :class="props.rail ? 'unselectable my-2' : ''"
                  :append-inner-icon="props.rail ? '' : 'mdi-menu-down'"
                >
                  <template #prepend-inner>
                    <v-icon
                      class="mr-6"
                      :icon="selectedPreset.icon"
                      :color="selectedPreset.color"
                      :size="props.rail ? 'default' : 'small'"
                    />
                  </template>
                </v-text-field>
              </template>
            </v-tooltip>
          </template>

          <v-list nav density="compact">
            <v-list-item :title="pinnedPreset.name" @click="selectPreset(pinnedPreset.id)">
              <template #prepend>
                <v-icon :icon="pinnedPreset.icon" />
              </template>
            </v-list-item>

            <template v-for="config in visibleCollections" :key="config.category">
              <v-list-subheader color="grey" class="bg-transparent" :title="config.category" />

              <v-list-item
                v-for="presetConfig in config.presets"
                :key="presetConfig.id"
                :title="presetConfig.name"
                @click="selectPreset(presetConfig.id)"
              >
                <template #prepend>
                  <v-icon :icon="presetConfig.icon" />
                </template>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>

        <div
          v-if="!props.rail && selectedPreset.id === 'pinned' && !ouraHubStore.accountPrefs?.pinnedServices?.length"
          class="text-body-medium py-2 px-6"
        >
          <p>
            You have no pinned items, you can pin items from the catalog page to have them appear here for quick access.
          </p>
          <p>
            To pin an item, go to the catalog page, find the item you want to pin and click the pin icon on the item
            card.
          </p>
          <p>
            Alternatively, you can use the ready shortcuts to quickly switch between different sets of apps / tools
            based on your needs.
          </p>
        </div>

        <div
          v-else-if="!props.rail && selectedPreset.id !== 'pinned' && !visibleServices?.length"
          class="text-body-medium py-2 px-6"
        >
          <p>None of the applications in the selected shortcuts group are yet added to the system.</p>
          <p>We are adding the existing tooling into this system during Oct / Nov so check again later.</p>
          <p>You can pick another shortcuts group or build your own shortcuts using the pinned items group.</p>
        </div>

        <template v-else>
          <template v-for="(group, gi) in visibleServices" :key="gi">
            <v-list-subheader
              v-if="!props.rail"
              color="grey"
              class="bg-transparent mt-2 mb-n1"
              :title="group.category"
            />

            <v-divider v-else class="mb-2" :class="gi === 0 ? '' : 'mt-2'" />

            <template v-for="(service, si) in group.services" :key="si">
              <v-tooltip location="right" :text="service.name" :disabled="!props.rail">
                <template #activator="{ props: tooltipProps }">
                  <v-list-item
                    v-bind="tooltipProps"
                    :title="service.name"
                    :prepend-icon="service.icon"
                    :disabled="!canLaunchPublic(service) && !needsPermissions(service)"
                    :class="!canLaunchPublic(service) && !needsPermissions(service) ? 'text-grey' : ''"
                    @click="!canLaunchPublic(service) ? (serviceDetails = service) : navigateToService(service)"
                  >
                    <template #append>
                      <v-icon
                        v-if="needsPermissions(service)"
                        size="small"
                        class="mx-n1"
                        color="warning"
                        icon="mdi-shield-alert-outline"
                      />
                      <v-icon
                        v-else-if="!canLaunchPublic(service)"
                        size="small"
                        color="error"
                        class="mx-n1"
                        icon="mdi-close-circle-outline"
                      />
                      <v-icon
                        v-else-if="service.context === 'web-service-ssr'"
                        size="small"
                        class="mx-n1"
                        :color="!canLaunchPublic(service) ? 'grey' : ''"
                      >
                        mdi-open-in-new
                      </v-icon>
                    </template>
                  </v-list-item>
                </template>
              </v-tooltip>
            </template>
          </template>
        </template>
      </v-list>

      <v-spacer />

      <v-list nav density="compact" class="flex-shrink-0">
        <v-divider class="mb-2" />

        <v-tooltip location="right" text="Report issue / feedback" :disabled="!props.rail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              prepend-icon="mdi-bug"
              title="Report issue / feedback"
              @click="openFeedbackDialog()"
            />
          </template>
        </v-tooltip>

        <v-divider class="my-2" />

        <v-tooltip location="right" text="Logout from OuraHub" :disabled="!props.rail">
          <template #activator="{ props: tooltipProps }">
            <v-list-item
              v-bind="tooltipProps"
              prepend-icon="mdi-logout"
              title="Logout from OuraHub"
              @click="emits('logout')"
            />
          </template>
        </v-tooltip>
      </v-list>
    </div>
  </v-navigation-drawer>

  <ServiceDetails
    v-if="serviceDetails"
    :id="serviceDetails.id"
    :focus-section="needsPermissions(serviceDetails) ? 'permissions' : 'versions'"
    @close="serviceDetails = undefined"
  />

  <ConfirmDialog ref="confirmDialog" />
</template>

<style lang="scss" scoped>
  .corner {
    position: fixed;
    top: 0;
    right: -15px;
    z-index: 1000;
    min-width: 16px;
    max-width: 16px;
    min-height: 16px;
    max-height: 16px;
    overflow: hidden;

    &:after {
      position: absolute;
      left: 2px;
      box-shadow: 0px -5px 1px 10px #202226;
      border-top-left-radius: 16px;
      background: transparent;
      min-width: 32px;
      min-height: 32px;
      overflow: auto;
      content: '\00a0\00a0\00a0';
    }
  }

  :deep(.unselectable) {
    padding: 0 4px;
    min-width: 52px;
    min-height: 48px;

    input {
      display: none !important;
    }

    .v-field {
      cursor: pointer !important;
    }

    .v-field__outline__end,
    .v-field__outline__start {
      flex-basis: 24px;
    }
  }

  .v-navigation-drawer--rail {
    :deep(.v-icon) {
      scale: 1.1;
    }

    .v-list-item {
      padding: 0 16px;
    }
  }

  .v-navigation-drawer {
    box-shadow: 3px 3px 3px rgba(0, 0, 0, 0.2) !important;
  }
</style>
